// **The bearer token, attached and recovered in one place** (`client-app`, design **D21**).
//
// This replaces `gate-reload.ts`, which is deleted rather than ported. That module detected a
// route contracted to return JSON answering with a non-JSON body — the shape a platform gate
// produced when it redirected an expired session toward a sign-in page. Nothing produces that
// shape now: an expired credential is a plain `401` from this server, so a heuristic keyed on
// response shape would have no remaining true positive to fire on, and every response it would
// still match is one of the ordinary failures the requirement explicitly excludes.
//
// # Why a `fetch` decorator rather than a check at each call site
//
// The rule has to live in **one** place so a route added later inherits it. Every rounds HTTP
// client in this app takes its `fetchImpl` from the composition root, so decorating that one value
// at the root *is* the single place — with no call site to edit and none that can be forgotten.
//
// # Why the retry is bounded per request, not per document
//
// A retry policy keyed on the response alone loops whenever re-acquisition keeps yielding a
// credential the server keeps refusing. One retry per request also cannot survive a page load, so
// unlike the module it replaces there is no cross-load guard to keep, and no session-storage
// marker that can be left poisoned.

/** MSAL, reduced to the three things this app asks of it. Injected so every scenario below is
 * assertable with no tenant, no network and no redirect. */
export interface TokenAcquirer {
  /** A token from the cached account, without user interaction. `null` when there is no account,
   * the refresh token has expired, or the tenant refuses — every one of which means "ask the
   * player". */
  acquireSilent(): Promise<string | null>;
  /** Begin an interactive sign-in. Returns once the flow has begun; in a browser this navigates. */
  signIn(): Promise<void>;
  /** End the authentication session and discard what is held locally (design **D20**). */
  signOut(): Promise<void>;
}

/** The inert default: a deployment with no tenant acquires nothing and never signs anybody in or
 * out. The `INERT_CONTROLLER` convention every other seam in this client already follows. */
export const INERT_ACQUIRER: TokenAcquirer = {
  acquireSilent: async () => null,
  signIn: async () => undefined,
  signOut: async () => undefined,
};

export interface BearerTokenDeps {
  acquirer: TokenAcquirer;
  /** Called when a request is refused **again** after a freshly acquired token. Retrying past this
   * point cannot succeed, so the caller is shown a signed-out state instead. */
  onSignedOut(): void;
}

/** `401`. Necessary for "this credential is spent", but **not sufficient** — see
 * {@link isEntraRefusal}. */
const UNAUTHORIZED = 401;

/**
 * Whether a response is *this server's Entra verifier* refusing the bearer token, as opposed to any
 * other `401` the API can answer.
 *
 * **The status alone is not the signal, and treating it as one was a real bug.** Waggle's own domain
 * credentials answer `401` as well — a missing or malformed membership bearer
 * (`lobby_http::unauthorized_401`) and a forged, unknown or wrong-scope run token
 * (`rounds_run_summary`). Keying the retry on the status meant every one of those was read as an
 * expired Entra token: the client re-acquired, silently **replayed the request** — duplicating a
 * non-idempotent POST — and when the second refusal arrived for the same domain reason, published a
 * signed-out state for a session Entra had never rejected.
 *
 * `server-entra-auth` puts `WWW-Authenticate: Bearer` on its own `401`s and only on those (RFC 6750
 * requires exactly that challenge of a bearer-protected resource), so this is a standards-compliant
 * marker rather than a private protocol.
 */
function isEntraRefusal(response: Response): boolean {
  if (response.status !== UNAUTHORIZED) {
    return false;
  }
  const challenge = response.headers.get('WWW-Authenticate');
  return challenge !== null && challenge.trimStart().toLowerCase().startsWith('bearer');
}

/**
 * The header the Entra access token rides — **not `Authorization`**, and that is the whole point.
 *
 * Every one of Waggle's own credentials already occupies `Authorization: Bearer` (a tournament
 * membership, the ordinal-0 host credential, a practice credential, a run token, a session token),
 * and this decorator wraps the *one* `fetchImpl` every client below the composition root is built
 * from. Setting `Authorization` here therefore did not add a credential — it **replaced** the one
 * the call site had just set, so on a gated deployment every membership-authorized request arrived
 * carrying the Entra token instead and was refused by a registry that had never seen it. Two
 * credentials, one header, and the wrapper always won.
 *
 * A dedicated header is the smaller of the two fixes: the alternative was moving all five of
 * Waggle's own credentials out of `Authorization` instead, across six client modules and every
 * server extractor. Non-standard, but this server is the only consumer of this token — and
 * `server-entra-auth` still answers `WWW-Authenticate: Bearer` on its own `401`s, which is what
 * {@link isEntraRefusal} keys the retry on.
 *
 * Kept in step with `waggle-server/src/entra_auth.rs`'s `ENTRA_TOKEN_HEADER`.
 */
export const ENTRA_TOKEN_HEADER = 'X-Waggle-Auth';

function withBearer(init: RequestInit | undefined, input: RequestInfo | URL, token: string | null) {
  const headers = new Headers(
    init?.headers ?? (input instanceof Request ? input.headers : undefined),
  );
  if (token !== null) {
    // The `Bearer ` scheme prefix is kept even though the header is our own: the token grammar is
    // then byte-identical to what the server already parsed, so relocating the credential changes
    // where it is read and nothing about how.
    headers.set(ENTRA_TOKEN_HEADER, `Bearer ${token}`);
  }
  return { ...init, headers };
}

/**
 * `fetchImpl`, wrapped so every request carries the current bearer token and a `401` is recovered
 * by **one** silent re-acquisition and **one** retry.
 *
 * Everything else passes through untouched, deliberately: a `503` is `server-entra-auth` reporting
 * that it cannot reach the tenant's signing keys, and acquiring a token for that would produce a
 * perfectly valid credential refused again for as long as the fault lasts — an unsatisfiable loop
 * dressed as a sign-in. A `403` means the credential *was* verified and rejected on its claims,
 * which the same credential cannot fix. A `5xx`, a network error and a malformed JSON body are
 * ordinary errors and are surfaced as themselves. **And a `401` that is not the Entra verifier's own
 * is one of those ordinary errors** — see {@link isEntraRefusal}.
 */
export function withBearerToken(fetchImpl: typeof fetch, deps: BearerTokenDeps): typeof fetch {
  let token: string | null = null;
  let primed = false;

  const current = async (): Promise<string | null> => {
    if (!primed) {
      primed = true;
      token = await deps.acquirer.acquireSilent();
    }
    return token;
  };

  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await fetchImpl(input, withBearer(init, input, await current()));
    if (!isEntraRefusal(response)) {
      return response;
    }
    const fresh = await deps.acquirer.acquireSilent();
    if (fresh === null) {
      // Silent acquisition failed: the player has to be asked. Surfacing the refusal instead would
      // hand them an error with no action attached to it.
      void deps.acquirer.signIn();
      return response;
    }
    token = fresh;
    const retried = await fetchImpl(input, withBearer(init, input, fresh));
    if (isEntraRefusal(retried)) {
      // A fresh credential the server's own verifier still refuses. Retrying again is the loop this
      // rule exists to make impossible. A domain `401` on the retry is not this, and must not sign
      // the player out.
      deps.onSignedOut();
    }
    return retried;
  };
}
